import { MediaSize } from '@ag-common-lib/public-api';

const MEDIA_SIZE_VALUES = new Set<string>(Object.values(MediaSize));

const CALENDAR_SAFE_HTML_TAGS = new Set([
  'P',
  'BR',
  'UL',
  'OL',
  'LI',
  'A',
  'IMG',
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'DIV',
]);

const HTML_TAG_PATTERN = /<[^>]+>/;

export interface CalendarEventDescriptionPayload {
  normalizedHtml: string;
  plainText: string;
  htmlContent: string;
}

export interface CalendarIcsDescriptionEvent {
  description?: string;
  htmlContent?: string;
}

export function buildCalendarEventDescription(
  description: string | null | undefined,
): CalendarEventDescriptionPayload | null {
  if (!description?.trim()) {
    return null;
  }

  const normalizedHtml = normalizeCalendarEventHtml(description);
  const plainText = htmlToPlainText(normalizedHtml);
  const htmlContent = buildCalendarHtmlContent(normalizedHtml);

  return {
    normalizedHtml,
    plainText,
    htmlContent,
  };
}

export function normalizeCalendarEventDescription(
  description: string | null | undefined,
): string | null | undefined {
  if (!description) {
    return description;
  }

  return normalizeCalendarEventHtml(description);
}

export function applyCalendarDescriptionToIcsEvent(event: CalendarIcsDescriptionEvent): void {
  if (!event.description) {
    return;
  }

  if (event.htmlContent && !hasHtmlMarkup(event.description)) {
    return;
  }

  const payload = buildCalendarEventDescription(event.description);
  if (!payload) {
    return;
  }

  event.description = payload.plainText;

  if (!event.htmlContent) {
    event.htmlContent = payload.htmlContent;
  }
}

function normalizeCalendarEventHtml(description: string): string {
  return description.replace(
    /(<img\b[^>]*\bsrc=["'])([^"']+)(["'])/gi,
    (_match, prefix: string, src: string, suffix: string) => `${prefix}${appendOriginToGetMediaSrc(src)}${suffix}`,
  );
}

function appendOriginToGetMediaSrc(src: string): string {
  if (!src.includes('getMedia')) {
    return src;
  }

  const [pathPart, ...queryParts] = src.split('?');
  const query = queryParts.length ? `?${queryParts.join('?')}` : '';
  const lastSegment = pathPart.split('/').filter(Boolean).pop();

  if (MEDIA_SIZE_VALUES.has(lastSegment)) {
    return src;
  }

  return `${pathPart}/${MediaSize.origin}${query}`;
}

function htmlToPlainText(html: string): string {
  if (!html) {
    return '';
  }

  if (!hasHtmlMarkup(html)) {
    return html.trim();
  }

  const container = createHtmlContainer(html);
  normalizeFormattedSpansToSemanticTags(container);
  prepareBoldTextForPlainText(container);
  prepareLinksForPlainText(container);
  prepareImagesForPlainText(container);
  prepareListsForPlainText(container);
  prepareBlockBreaks(container);

  return normalizePlainText(container.textContent ?? '');
}

function buildCalendarHtmlContent(html: string): string {
  if (!html) {
    return '';
  }

  if (!hasHtmlMarkup(html)) {
    return `<!DOCTYPE html><html><body><p>${escapeHtml(html)}</p></body></html>`;
  }

  const sanitizedHtml = sanitizeCalendarHtml(html);
  return `<!DOCTYPE html><html><body>${sanitizedHtml}</body></html>`;
}

function sanitizeCalendarHtml(html: string): string {
  const container = createHtmlContainer(html);
  normalizeFormattedSpansToSemanticTags(container);
  unwrapUnsupportedElements(container);
  removeUnsupportedAttributes(container);
  removeUnsupportedTags(container);
  finalizeBoldTextForCalendarHtml(container);

  return container.innerHTML.trim();
}

function finalizeBoldTextForCalendarHtml(container: HTMLElement): void {
  prepareBoldTextForPlainText(container);

  container.querySelectorAll('strong, b').forEach(element => {
    element.replaceWith(...Array.from(element.childNodes));
  });
}

function normalizeFormattedSpansToSemanticTags(container: HTMLElement): void {
  container.querySelectorAll('span').forEach(span => {
    const style = span.getAttribute('style') ?? '';
    const fontWeight = getStyleProperty(style, 'font-weight');

    if (!isBoldFontWeight(fontWeight)) {
      return;
    }

    const strong = document.createElement('strong');
    while (span.firstChild) {
      strong.appendChild(span.firstChild);
    }

    span.replaceWith(strong);
  });
}

function prepareBoldTextForPlainText(container: HTMLElement): void {
  container.querySelectorAll('strong, b').forEach(element => {
    const textNodes = collectTextNodes(element);
    textNodes.forEach(textNode => {
      textNode.textContent = toUnicodeBold(textNode.textContent ?? '');
    });
  });
}

function collectTextNodes(element: Element): Text[] {
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);

  let currentNode = walker.nextNode();
  while (currentNode) {
    textNodes.push(currentNode as Text);
    currentNode = walker.nextNode();
  }

  return textNodes;
}

function toUnicodeBold(text: string): string {
  return Array.from(text, char => toUnicodeBoldChar(char)).join('');
}

function toUnicodeBoldChar(char: string): string {
  const code = char.charCodeAt(0);

  if (code >= 0x41 && code <= 0x5a) {
    return String.fromCodePoint(0x1d400 + (code - 0x41));
  }

  if (code >= 0x61 && code <= 0x7a) {
    return String.fromCodePoint(0x1d41a + (code - 0x61));
  }

  if (code >= 0x30 && code <= 0x39) {
    return String.fromCodePoint(0x1d7ce + (code - 0x30));
  }

  return char;
}

function getStyleProperty(style: string, propertyName: string): string {
  const propertyPattern = new RegExp(`(?:^|;)\\s*${propertyName}\\s*:\\s*([^;]+)`, 'i');
  const match = style.match(propertyPattern);
  return match?.[1]?.trim().toLowerCase() ?? '';
}

function isBoldFontWeight(fontWeight: string): boolean {
  if (!fontWeight) {
    return false;
  }

  if (fontWeight === 'bold' || fontWeight === 'bolder') {
    return true;
  }

  const numericWeight = Number.parseInt(fontWeight, 10);
  return !Number.isNaN(numericWeight) && numericWeight >= 600;
}

function prepareLinksForPlainText(container: HTMLElement): void {
  container.querySelectorAll('a').forEach(anchor => {
    const href = anchor.getAttribute('href')?.trim();
    const label = anchor.textContent?.trim();

    if (!href) {
      anchor.replaceWith(document.createTextNode(label ?? ''));
      return;
    }

    if (label && label !== href) {
      anchor.replaceWith(document.createTextNode(`${label} (${href})`));
      return;
    }

    anchor.replaceWith(document.createTextNode(href));
  });
}

function prepareImagesForPlainText(container: HTMLElement): void {
  container.querySelectorAll('img').forEach(image => {
    const src = image.getAttribute('src')?.trim();
    image.replaceWith(document.createTextNode(src ? `[Image: ${src}]` : ''));
  });
}

function prepareListsForPlainText(container: HTMLElement): void {
  container.querySelectorAll('ol').forEach(list => {
    list.querySelectorAll(':scope > li').forEach((item, index) => {
      item.insertAdjacentText('afterbegin', `${index + 1}. `);
    });
  });

  container.querySelectorAll('ul').forEach(list => {
    list.querySelectorAll(':scope > li').forEach(item => {
      item.insertAdjacentText('afterbegin', '- ');
    });
  });
}

function prepareBlockBreaks(container: HTMLElement): void {
  container.querySelectorAll('br').forEach(lineBreak => {
    lineBreak.replaceWith(document.createTextNode('\n'));
  });

  container.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6').forEach(block => {
    block.appendChild(document.createTextNode('\n'));
  });
}

function unwrapUnsupportedElements(container: HTMLElement): void {
  container.querySelectorAll('font, span').forEach(element => {
    element.replaceWith(...Array.from(element.childNodes));
  });
}

function removeUnsupportedAttributes(container: HTMLElement): void {
  container.querySelectorAll('*').forEach(element => {
    element.removeAttribute('style');
    element.removeAttribute('class');
    element.removeAttribute('align');
  });
}

function removeUnsupportedTags(container: HTMLElement): void {
  const elements = Array.from(container.querySelectorAll('*')).reverse();

  elements.forEach(element => {
    if (CALENDAR_SAFE_HTML_TAGS.has(element.tagName)) {
      return;
    }

    element.replaceWith(document.createTextNode(element.textContent ?? ''));
  });
}

function createHtmlContainer(html: string): HTMLElement {
  const container = document.createElement('div');
  container.innerHTML = html;
  return container;
}

function hasHtmlMarkup(value: string): boolean {
  return HTML_TAG_PATTERN.test(value);
}

function normalizePlainText(value: string): string {
  return value.replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
